import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Col, DatePicker, Empty, Grid, Input, Modal, Radio, Row, Space, Spin, Typography } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { DownOutlined, FilterOutlined, SearchOutlined, UpOutlined } from '@ant-design/icons';

import { StyledLinkedTable } from '../../common/tableView.styles';
import { pathToUrl } from '../../utils/router';
import { PageRoutesEnum } from '../../utils/routes';

import {
  mapSimilarProjectsApiError,
  SimilarProjectHitDto,
  SimilarProjectsApiError,
  useGetSimilarProjects,
} from './useGetSimilarProjects';

const StyledModal = styled(Modal)`
  &.ant-modal {
    width: min(1040px, 95vw) !important;
    margin: 12px auto;
  }

  .ant-modal-content {
    border-radius: 12px;
    padding: 16px 16px 12px;
    max-height: calc(100vh - 24px);
    display: flex;
    flex-direction: column;
  }

  .ant-modal-header {
    margin-bottom: 12px;
  }

  .ant-modal-body {
    padding: 0;
    min-height: 0;
    overflow: hidden;
  }

  @media (max-width: 576px) {
    &.ant-modal {
      width: calc(100vw - 16px) !important;
      margin: 8px auto;
    }

    .ant-modal-content {
      padding: 12px;
      max-height: calc(100vh - 16px);
    }
  }

  @media (min-width: 768px) {
    .ant-modal-content {
      padding: 20px 24px 16px;
    }
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
`;

const FiltersCard = styled.div`
  border: 1px solid #e9eff6;
  border-radius: 10px;
  padding: 10px;
  background: #fafcff;
`;

const ActionRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  align-items: center;

  @media (max-width: 576px) {
    flex-wrap: wrap;
  }
`;

const LeftActions = styled(Space)`
  flex-wrap: wrap;

  @media (max-width: 576px) {
    width: 100%;
  }
`;

const SearchActionButton = styled(Button)`
  @media (max-width: 576px) {
    width: 100%;
  }
`;

const SourceSummary = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  border: 1px solid #e9eff6;
  border-radius: 8px;
  padding: 8px 10px;
  background: #fafcff;
  min-width: 0;
`;

const SourceValue = styled(Typography.Text)`
  &.ant-typography {
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const SourceInputSection = styled.div<{ $source: 'project' | 'query' }>`
  display: flex;
  flex-direction: column;
  gap: 8px;

  min-height: ${({ $source }) => ($source === 'query' ? '118px' : 'auto')};

  @media (max-width: 576px) {
    min-height: ${({ $source }) => ($source === 'query' ? '96px' : 'auto')};
  }
`;

const ResultsContainer = styled.div<{ $filtersVisible: boolean }>`
  max-height: ${({ $filtersVisible }) => ($filtersVisible ? '52vh' : '70vh')};
  min-height: ${({ $filtersVisible }) => ($filtersVisible ? '320px' : '360px')};
  overflow: hidden;
  padding-right: 4px;

  ::-webkit-scrollbar {
    width: 6px;
  }

  ::-webkit-scrollbar-track {
    background: #f5f5f5;
  }

  ::-webkit-scrollbar-thumb {
    background-color: #bdbdbd;
  }

  @media (max-width: 576px) {
    max-height: ${({ $filtersVisible }) => ($filtersVisible ? '44vh' : '54vh')};
    min-height: ${({ $filtersVisible }) => ($filtersVisible ? '190px' : '220px')};
    padding-right: 0;
  }
`;

const LinkCell = styled.div`
  width: 100%;
  padding: 12px;
  cursor: pointer;
  white-space: normal;
  word-break: break-word;

  &:hover {
    text-decoration: underline;
  }

  @media (max-width: 576px) {
    padding: 10px 8px;
  }
`;

const CellContent = styled.div`
  padding: 12px;
  white-space: normal;
  word-break: break-word;

  @media (max-width: 576px) {
    padding: 10px 8px;
  }
`;

const ProjectNameLinkCell = styled(Link)`
  display: block;
  color: #1677ff;

  &:hover {
    color: #1677ff;
  }
`;

const ResultsHint = styled(Typography.Text)`
  display: block;
  margin-bottom: 4px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ResultsMeta = styled.div`
  min-height: 48px;
  margin-bottom: 8px;

  @media (max-width: 576px) {
    min-height: 44px;
  }
`;

const LoadingState = styled.div<{ $filtersVisible: boolean }>`
  min-height: ${({ $filtersVisible }) => ($filtersVisible ? '340px' : '420px')};
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 576px) {
    min-height: ${({ $filtersVisible }) => ($filtersVisible ? '190px' : '220px')};
  }
`;

const TOP_K = 20;
const DEFAULT_ACTIVE_OR_CLOSED_AFTER_MONTHS = 6;
const DESCRIPTION_PREVIEW_LENGTH = 220;
const TABLE_COLUMN_WIDTHS = {
  match: 88,
  project: 168,
  description: 420,
  status: 96,
  clientCvCount: 80,
  recruitmentCount: 104,
} as const;

const getDefaultActiveOrClosedAfter = () => dayjs().subtract(DEFAULT_ACTIVE_OR_CLOSED_AFTER_MONTHS, 'month').startOf('day');

const truncateDescription = (value: string) => {
  if (value.length <= DESCRIPTION_PREVIEW_LENGTH) {
    return value;
  }

  const rawPreview = value.slice(0, DESCRIPTION_PREVIEW_LENGTH).trimEnd();
  const lastWordBoundary = rawPreview.lastIndexOf(' ');
  const safePreview =
    lastWordBoundary > DESCRIPTION_PREVIEW_LENGTH * 0.6 ? rawPreview.slice(0, lastWordBoundary).trimEnd() : rawPreview;

  return `${safePreview}...`;
};

const DescriptionPreview = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;

const DescriptionText = styled.span`
  display: block;
  line-height: 1.5;
  white-space: normal;
  word-break: break-word;
`;

const DescriptionToggle = styled(Button)`
  &.ant-btn-link {
    padding: 0;
    height: auto;
    color: #1677ff;
    font-weight: 500;
  }
`;

const ExpandableDescription: React.FC<{ resetKey: number; value?: string | null }> = ({ resetKey, value }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    setIsExpanded(false);
  }, [resetKey]);

  if (!value) {
    return <CellContent>-</CellContent>;
  }

  const isLongDescription = value.length > DESCRIPTION_PREVIEW_LENGTH;
  const visibleValue = isExpanded || !isLongDescription ? value : truncateDescription(value);

  return (
    <CellContent>
      <DescriptionPreview>
        <DescriptionText>{visibleValue}</DescriptionText>
        {isLongDescription ? (
          <DescriptionToggle
            type="link"
            icon={isExpanded ? <UpOutlined aria-hidden /> : <DownOutlined aria-hidden />}
            onClick={() => setIsExpanded((current) => !current)}
          >
            {isExpanded ? 'Show less' : 'Show more'}
          </DescriptionToggle>
        ) : null}
      </DescriptionPreview>
    </CellContent>
  );
};

const renderProjectLinkCell = (content: React.ReactNode, row: SimilarProjectHitDto, onClose: () => void) => {
  return (
    <ProjectNameLinkCell to={pathToUrl(PageRoutesEnum.projectsEdit, { id: row.project.uuid })} onClick={onClose}>
      <LinkCell>{content}</LinkCell>
    </ProjectNameLinkCell>
  );
};

type SimilarProjectsModalProps = {
  open: boolean;
  onClose: () => void;
  defaultSource?: 'project' | 'query';
  allowProjectSource?: boolean;
  projectId?: string;
  projectName?: string;
};

export const SimilarProjectsModal: React.FC<SimilarProjectsModalProps> = ({
  open,
  onClose,
  defaultSource = 'project',
  allowProjectSource = true,
  projectId,
  projectName,
}) => {
  const screens = Grid.useBreakpoint();
  const isMobileViewport = !screens.sm;
  const isCompactViewport = !screens.md;

  const canUseProjectSource = Boolean(projectId) && allowProjectSource;
  const resolvedDefaultSource = canUseProjectSource ? defaultSource : 'query';

  const [source, setSource] = useState<'project' | 'query'>(resolvedDefaultSource);
  const [queryText, setQueryText] = useState('');
  const [activeOrClosedAfter, setActiveOrClosedAfter] = useState<Dayjs | null>(() => getDefaultActiveOrClosedAfter());
  const [hasSearched, setHasSearched] = useState(false);
  const [results, setResults] = useState<SimilarProjectHitDto[]>([]);
  const [searchRunKey, setSearchRunKey] = useState(0);
  const [areFiltersVisible, setAreFiltersVisible] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [requestError, setRequestError] = useState<SimilarProjectsApiError | null>(null);

  const filters = useMemo(
    () => ({
      activeOrClosedAfter,
    }),
    [activeOrClosedAfter]
  );

  const { isFetching, refetch } = useGetSimilarProjects({
    source: source === 'project' ? { mode: 'project', projectId } : { mode: 'query', query: queryText },
    filters,
    enabled: false,
  });

  const clearErrors = useCallback(() => {
    setValidationError(null);
    setRequestError(null);
  }, []);

  const clearFilters = useCallback(() => {
    setActiveOrClosedAfter(null);
    clearErrors();
  }, [clearErrors]);

  const handleSourceChange = useCallback(
    (nextSource: 'project' | 'query') => {
      setSource(nextSource);
      setHasSearched(false);
      setResults([]);
      clearErrors();
    },
    [clearErrors]
  );

  const executeSearch = useCallback(async () => {
    clearErrors();

    if (source === 'query' && !queryText.trim()) {
      setValidationError('Query is required for similarity search.');
      return;
    }

    setHasSearched(true);
    setSearchRunKey((current) => current + 1);
    const result = await refetch();
    if (result.error) {
      setResults([]);
      setRequestError(mapSimilarProjectsApiError(result.error));
    } else {
      setResults(result.data ?? []);
    }
    setAreFiltersVisible(false);
  }, [clearErrors, queryText, refetch, source]);

  const handleQueryKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        event.preventDefault();
        void executeSearch();
      }
    },
    [executeSearch]
  );

  const activeSourceLabel = source === 'project' ? projectName ?? 'Current Project' : 'Text Query';

  useEffect(() => {
    if (!open) return;
    setSource(resolvedDefaultSource);
    setQueryText('');
    setActiveOrClosedAfter(getDefaultActiveOrClosedAfter());
    setHasSearched(false);
    setResults([]);
    setSearchRunKey(0);
    setAreFiltersVisible(false);
    clearErrors();
  }, [clearErrors, open, resolvedDefaultSource]);

  useEffect(() => {
    if (!open) return;
    clearErrors();
  }, [activeOrClosedAfter, clearErrors, open, queryText, source]);

  useEffect(() => {
    if (!open) return;
    if (source !== 'project') return;
    if (!canUseProjectSource) return;
    if (hasSearched) return;
    if (!activeOrClosedAfter) return;
    void executeSearch();
  }, [activeOrClosedAfter, canUseProjectSource, executeSearch, hasSearched, open, source]);

  const columns = useMemo(
    () => [
      {
        title: 'Match ↓',
        dataIndex: 'score',
        key: 'score',
        width: TABLE_COLUMN_WIDTHS.match,
        render: (value: number) => <CellContent>{`${(value * 100).toFixed(1)}%`}</CellContent>,
      },
      {
        title: 'Project',
        dataIndex: ['project', 'name'],
        key: 'project',
        width: TABLE_COLUMN_WIDTHS.project,
        render: (_: string, row: SimilarProjectHitDto) => renderProjectLinkCell(row.project.name, row, onClose),
      },
      {
        title: 'Description',
        dataIndex: ['project', 'description'],
        key: 'description',
        width: TABLE_COLUMN_WIDTHS.description,
        render: (value: string) => <ExpandableDescription resetKey={searchRunKey} value={value} />,
      },
      {
        title: 'Status',
        dataIndex: ['project', 'active'],
        key: 'status',
        width: TABLE_COLUMN_WIDTHS.status,
        render: (value: boolean) => <CellContent>{value ? 'Active' : 'Closed'}</CellContent>,
      },
      {
        title: 'Sent CVs',
        dataIndex: ['project', 'clientCvCount'],
        key: 'clientCvCount',
        width: TABLE_COLUMN_WIDTHS.clientCvCount,
        render: (value: number | null | undefined) => <CellContent>{value ?? '-'}</CellContent>,
      },
      {
        title: 'Recruitments',
        dataIndex: ['project', 'recruitmentCount'],
        key: 'recruitmentCount',
        width: TABLE_COLUMN_WIDTHS.recruitmentCount,
        render: (value: number | null | undefined) => <CellContent>{value ?? '-'}</CellContent>,
      },
    ],
    [onClose, searchRunKey]
  );

  const shouldShowAutoProjectLoading = source === 'project' && canUseProjectSource && !hasSearched;
  const shouldReserveResultsMeta = hasSearched && (isFetching || results.length > 0);
  const shouldShowResultsMeta = hasSearched && results.length > 0;
  const tableScrollY = useMemo(() => {
    if (isMobileViewport) {
      return areFiltersVisible ? 190 : 230;
    }

    if (isCompactViewport) {
      return areFiltersVisible ? 260 : 330;
    }

    return areFiltersVisible ? 340 : 420;
  }, [areFiltersVisible, isCompactViewport, isMobileViewport]);

  return (
    <StyledModal open={open} title="Project Similarity Search" onCancel={onClose} footer={null} centered>
      <Content>
        {canUseProjectSource ? (
          <Radio.Group
            value={source}
            onChange={(event) => handleSourceChange(event.target.value)}
            optionType="button"
            buttonStyle="solid"
            disabled={isFetching}
          >
            <Radio.Button value="project">Current Project</Radio.Button>
            <Radio.Button value="query">Text Query</Radio.Button>
          </Radio.Group>
        ) : null}

        <SourceSummary>
          <Typography.Text type="secondary">Search Source:</Typography.Text>
          <SourceValue strong title={activeSourceLabel}>
            {activeSourceLabel}
          </SourceValue>
        </SourceSummary>

        <SourceInputSection $source={source}>
          {source === 'query' ? (
            <>
              <Typography.Text type="secondary">Query</Typography.Text>
              <Input.TextArea
                value={queryText}
                onChange={(event) => setQueryText(event.target.value)}
                onKeyDown={handleQueryKeyDown}
                placeholder="Describe role, stack, domain, or requirements"
                rows={isMobileViewport ? 3 : 4}
                disabled={isFetching}
              />
            </>
          ) : (
            <Typography.Text type="secondary">
              Search will run using the current project as source context.
            </Typography.Text>
          )}
        </SourceInputSection>

        <ActionRow>
          <LeftActions size={[8, 8]}>
            <Button
              icon={<FilterOutlined aria-hidden />}
              onClick={() => setAreFiltersVisible((value) => !value)}
              disabled={isFetching}
            >
              {`Filters${activeOrClosedAfter ? ' (1)' : ''}`}
            </Button>
            <Button onClick={clearFilters} disabled={isFetching || !activeOrClosedAfter}>
              Clear
            </Button>
          </LeftActions>
          <SearchActionButton
            type="primary"
            icon={<SearchOutlined aria-hidden />}
            onClick={() => void executeSearch()}
            loading={isFetching}
            disabled={isFetching}
          >
            Search
          </SearchActionButton>
        </ActionRow>

        {areFiltersVisible && (
          <FiltersCard>
            <Row gutter={[12, 12]}>
              <Col xs={24} md={12}>
                <Typography.Text type="secondary">Active or closed after</Typography.Text>
                <div style={{ marginTop: 6 }}>
                  <DatePicker
                    style={{ width: '100%' }}
                    format="YYYY-MM-DD"
                    value={activeOrClosedAfter ?? undefined}
                    onChange={(value) => setActiveOrClosedAfter(value)}
                    disabled={isFetching}
                  />
                </div>
              </Col>
              <Col xs={24} md={12}>
                <Typography.Text type="secondary">Top results</Typography.Text>
                <div style={{ marginTop: 6 }}>
                  <Typography.Text>{TOP_K} (fixed)</Typography.Text>
                </div>
              </Col>
            </Row>
          </FiltersCard>
        )}

        <ResultsContainer $filtersVisible={areFiltersVisible}>
          {validationError && (
            <Alert
              style={{ marginBottom: 12 }}
              type="error"
              showIcon
              message={validationError}
            />
          )}

          {requestError && (
            <Alert
              style={{ marginBottom: 12 }}
              type={requestError.kind === 'source-not-indexed' ? 'warning' : 'error'}
              showIcon
              message={requestError.message}
              action={
                requestError.canRetry ? (
                  <Button size="small" onClick={() => void executeSearch()} disabled={isFetching}>
                    Retry
                  </Button>
                ) : undefined
              }
            />
          )}

          {shouldReserveResultsMeta && (
            <ResultsMeta>
              {shouldShowResultsMeta && (
                <>
                  {results.length > 0 ? (
                    <>
                      <ResultsHint type="secondary">
                        {`Showing ${results.length} result${results.length === 1 ? '' : 's'}.`}
                      </ResultsHint>
                      <ResultsHint type="secondary">
                        Tip: Click a project name to open details.{isFetching ? ' Updating results...' : ''}
                      </ResultsHint>
                    </>
                  ) : null}
                </>
              )}
            </ResultsMeta>
          )}

          {shouldShowAutoProjectLoading || (results.length === 0 && isFetching) ? (
            <LoadingState $filtersVisible={areFiltersVisible}>
              <Spin tip="Loading similar projects..." />
            </LoadingState>
          ) : !hasSearched ? (
            <Empty description={source === 'query' ? 'Enter query and run search' : 'Run search to see results'} />
          ) : results.length === 0 ? (
            <Empty description="No matching projects found. Try using fewer filters">
              {activeOrClosedAfter && (
                <Button onClick={clearFilters} size="small">
                  Clear Filters
                </Button>
              )}
            </Empty>
          ) : (
            <StyledLinkedTable
              rowKey={(row: SimilarProjectHitDto) => row.project.uuid}
              columns={columns}
              dataSource={results}
              pagination={false}
              size="small"
              bordered
              sticky
              scroll={{
                y: tableScrollY,
                x: 'max-content',
              }}
            />
          )}
        </ResultsContainer>
      </Content>
    </StyledModal>
  );
};
